/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { ProvideTimers, TimerProps } from '@blend/shared-react';
import { ActionButton } from '@blend/enterprise-components';
import { useDispatch } from 'react-redux';
import { notaryAPI } from '../../../../api/notary';
import { CommonAPI } from '../../../../api/common/responses';
import { addNotification } from '../../../../actions/pageActions';
import { EventHandler } from '../../../../api/websocket/types';
import { useWebsocketEventSubscriber } from '../../../../hooks/useWebsocketEventSubscriber';
import { columnConfigurations } from './configs';
import { Header, Table } from '@components/common/Pipeline';
import { NotificationItem } from '@components/common/Notifications/NotificationItem';
import { WEBSOCKET_CHANNELS } from '@shared/constants/websocketChannels';
import { getConfig } from '@utils/configs';
import { uniqBy } from '@utils/lodash';

export const activityLogDownloadErrorNotificationItem: NotificationItem.PropsWithoutId = {
  type: 'error',
  iconType: 'error',
  content: 'Could not download activity log.',
  shouldAutoRemove: true,
};

const eventRefreshInterval = 1500;

const styles = StyleSheet.create({
  wrapper: {
    padding: '0 40px',
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 40,
  },
});

export namespace RoomEventTab {
  export interface Props {
    roomId: string;
  }
}

export const _ActivityTab: React.FC<RoomEventTab.Props & TimerProps> = ({ roomId, reactSafeSetTimeout }) => {
  const dispatch = useDispatch();
  const [events, setEvents] = useState<CommonAPI.RoomEvent[]>([]);

  const downloadActivityLog = async () => {
    try {
      await notaryAPI.rooms.fetchRoomEventsPdf(roomId);
    } catch (err) {
      addNotification(activityLogDownloadErrorNotificationItem)(dispatch);
    }
  };

  const websocketEventHandler = useCallback<EventHandler<CommonAPI.RoomEvent>>(event => {
    // add the new event to the list of events (unique by id to avoid duplicates from race condition)
    setEvents(prevEvents => uniqBy([event, ...prevEvents], 'id'));
  }, []);
  useWebsocketEventSubscriber(WEBSOCKET_CHANNELS.roomEvents(roomId), websocketEventHandler, {
    enabled: getConfig().isWebsocketEnabled,
  });

  useEffect(() => {
    let timeout: number | undefined;
    const fetchEvents = async () => {
      const fetchedEvents = await notaryAPI.rooms.fetchRoomEvents(roomId);
      setEvents(fetchedEvents);
      // Disable polling if websocket is enabled
      if (!getConfig().isWebsocketEnabled) {
        timeout = reactSafeSetTimeout(() => {
          fetchEvents();
        }, eventRefreshInterval);
      }
    };

    fetchEvents();
    return () => clearTimeout(timeout);
  }, [roomId]);

  return (
    <div>
      <div className={css(styles.headerWrapper)}>
        <Header header="Activity" />
        <ActionButton
          data-dd-privacy="allow"
          data-it="download-activity-log-button"
          btnSize="md"
          label="Download log"
          onClick={downloadActivityLog}
          type="button"
        />
      </div>
      <Table data-it="activityTable" data={events} tableColumns={columnConfigurations} />
    </div>
  );
};

export const ActivityTab: React.FC<RoomEventTab.Props> = props => (
  <ProvideTimers render={timerProps => <_ActivityTab {...props} {...timerProps} />} />
);
